"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { Edit, Mail, Phone, MapPin, Calendar, Loader2, Award, CheckCircle } from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import { API_BASE_URL, formationAPI } from "../../services/api"

// Add interface for completed formations
interface CompletedFormation {
  id: number;
  name: string;
  image: string | null;
  categoryName: string | null;
  progress: string;
  lastUpdate: string;
  status: string;
  completedAt: string;
}

export default function DashboardAbout() {
  const { user } = useAuth();
  
  // Add state for completed formations
  const [completedFormations, setCompletedFormations] = useState<CompletedFormation[]>([]);
  const [isLoadingFormations, setIsLoadingFormations] = useState(true);
  const [formationsError, setFormationsError] = useState<string | null>(null);

  // Generate avatar with first letter of first name
  const generateAvatarWithInitials = (firstName: string) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 128;
    canvas.height = 128;

    if (ctx) {
      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 128, 128);
      gradient.addColorStop(0, '#667eea');
      gradient.addColorStop(1, '#764ba2');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 128, 128);

      // Text
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(firstName.charAt(0).toUpperCase(), 64, 64);
    }

    return canvas.toDataURL();
  };

  // Get current photo to display
  const getCurrentPhoto = () => {
    if (user?.photo) {
      // Backend uploads served at /uploads endpoint
      return `${API_BASE_URL}${user.photo}`;
    }
    if (user?.firstName) {
      return generateAvatarWithInitials(user.firstName);
    }
    return "/placeholder.svg";
  };

  // Format join date
  const formatJoinDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  // Format completion date
  const formatCompletionDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long',
      day: 'numeric'
    });
  };

  // Fetch completed formations
  useEffect(() => {
    const fetchCompletedFormations = async () => {
      if (!user?.id) return;
      
      try {
        setIsLoadingFormations(true);
        setFormationsError(null);
        
        const response = await formationAPI.getCompletedFormations(user.id);
        
        if (response.data.success) {
          setCompletedFormations(response.data.data);
        } else {
          setFormationsError("Erreur lors du chargement des formations");
        }
      } catch (error) {
        console.error("Erreur lors du chargement des formations complétées:", error);
        setFormationsError("Erreur lors du chargement des formations");
      } finally {
        setIsLoadingFormations(false);
      }
    };

    fetchCompletedFormations();
  }, [user?.id]);

  // Loading state while user data is being fetched
  if (!user) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="container px-4 py-8 md:px-6 md:py-12">
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="text-3xl font-serif font-bold tracking-tight">Mon profil</h1>
          <p className="text-gray-500 dark:text-gray-400">Consultez et gérez vos informations personnelles</p>
        </div>

        <div className="grid gap-6 md:grid-cols-12">
          {/* Carte de profil */}
          <Card className="md:col-span-4">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="relative w-32 h-32">
                  <img
                    src={getCurrentPhoto()}
                    alt={`${user.firstName} ${user.lastName}`}
                    className="rounded-full object-cover border-4 border-white shadow-md w-full h-full absolute inset-0"
                  />
                </div>
              </div>
              <CardTitle className="text-2xl">{`${user.firstName} ${user.lastName}`}</CardTitle>
              <CardDescription>
                Membre depuis {user.createdAt ? formatJoinDate(user.createdAt) : ""}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-center">
                <Button variant="outline" size="sm" asChild>
                  <a href="/dashboard/profile">
                    <Edit className="mr-2 h-4 w-4" />
                    Modifier le profil
                  </a>
                </Button>
              </div>
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-start gap-2">
                  <Mail className="h-5 w-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                  </div>
                </div>
                {user.phone && (
                  <div className="flex items-start gap-2">
                    <Phone className="h-5 w-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Téléphone</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{user.phone}</p>
                    </div>
                  </div>
                )}
                {user.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-5 w-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Adresse</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{user.address}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <Calendar className="h-5 w-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Membre depuis</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {user.createdAt ? formatJoinDate(user.createdAt) : ""}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Informations détaillées */}
          <div className="md:col-span-8 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>À propos de moi</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 dark:text-gray-300">
                  {user.bio || "Aucune biographie disponible."}
                </p>
              </CardContent>
            </Card>

            {/* Completed Formations Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-green-600" />
                  Formations complétées
                </CardTitle>
                <CardDescription>
                  {completedFormations.length > 0 
                    ? `Vous avez complété ${completedFormations.length} formation${completedFormations.length > 1 ? 's' : ''}`
                    : "Aucune formation complétée pour le moment"
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingFormations ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="ml-2 text-sm text-gray-500">Chargement des formations...</span>
                  </div>
                ) : formationsError ? (
                  <div className="text-center py-8">
                    <p className="text-red-500 text-sm">{formationsError}</p>
                  </div>
                ) : completedFormations.length === 0 ? (
                  <div className="text-center py-8">
                    <Award className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Aucune formation complétée</p>
                    <p className="text-gray-400 text-xs mt-1">
                      Complétez vos formations pour les voir apparaître ici
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {completedFormations.map((formation) => (
                      <div key={formation.id} className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="relative w-16 h-16 flex-shrink-0">
                          <img
                            src={formation.image || "/placeholder.svg"}
                            alt={formation.name}
                            className="rounded-md object-cover w-full h-full"
                          />
                          <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1">
                            <CheckCircle className="h-3 w-3 text-white" />
                          </div>
                        </div>
                        <div className="flex-grow min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{formation.name}</h3>
                          {formation.categoryName && (
                            <p className="text-sm text-gray-500 mt-1">{formation.categoryName}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            <div className="flex items-center gap-1">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-sm text-green-600 font-medium">Complétée</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-500">
                                {formatCompletionDate(formation.completedAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {completedFormations.length > 3 && (
                      <div className="text-center pt-2">
                        <Button variant="outline" size="sm" asChild>
                          <a href="/dashboard/formations">
                            Voir toutes mes formations
                          </a>
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* <Card>
              <CardHeader>
                <CardTitle>Centres d'intérêt</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200">
                    Droits humains
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200">
                    Égalité de genre
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200">
                    Justice sociale
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200">
                    Éducation
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200">
                    Plaidoyer
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Certifications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="relative w-12 h-12 flex-shrink-0">
                      <img
                        src="/placeholder.svg?height=50&width=50"
                        alt="Certification"
                        className="rounded-md object-cover w-full h-full absolute inset-0"
                      />
                    </div>
                    <div>
                      <h3 className="font-medium">Introduction aux droits humains</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Obtenue le 15 juin 2023</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="relative w-12 h-12 flex-shrink-0">
                      <img
                        src="/placeholder.svg?height=50&width=50"
                        alt="Certification"
                        className="rounded-md object-cover w-full h-full absolute inset-0"
                      />
                    </div>
                    <div>
                      <h3 className="font-medium">Égalité de genre : enjeux et perspectives</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Obtenue le 3 juillet 2023</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card> */}
          </div>
        </div>
      </div>
    </div>
  )
}